import React, { useEffect, useState } from 'react';
import {
  App, Button, Form, Tag,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { useMessageError } from '../../../../hooks/common';
import SinglePageContainer from '../../../Common/SinglePageContainer';

import {
  BusinessUpdateParams,
  useBusinessPartnerGetById,
  BusinessPartnerWithContracts,
  BusinessPartnerTableRow,
  useUpdateBusinessPartner,
  useUpdatePhoneNumber,
  useUpdateEmailAddress, IUpdateOrCreatePhoneNumberParams, useUpdateBillingAddress,
} from '../../../../hooks/api/businessPartners';

import { DeleteButton, SyncBusinessButton } from '../actions';
import { useUnsavedChanges } from '../../../../context/unsavedChanges';
import { createSearchParamsString, getLatestBusinessPhoneNumber, isObjEqual } from '../../../../utils';
import BusinessFormItems from '../Form';
import { getBusinessStatusColor } from '../index';
import { dateToSave } from '../../../../utils/date';
import { LEGAL_FORM_VALUES, SALUTATION_VALUES, TITLE_VALUES } from '../../../../types/businessPartnerRelated';
import ContractsList from '../Form/Contracts';
import SyncNotification from './SyncNotification';
import { useAuth } from '../../../../store/auth';
import { UserRole } from '../../../../enums/user';

const initialState: BusinessUpdateParams = {
  personalData: {
    businessPartner: {
      businessPartnerNumber: '',

      newSalutation: 'None',
      newTitle: 'None',
      newFirstname: '',
      newLastname: '',
      newDateOfBirth: dayjs(), // new Date(businessPartner.dateOfBirth).toISOString() // - Send as string
      newLegalForm: 'None',
    },
    phoneNumber: {
      advertisementConsentROList: [],
      creationDate: dateToSave(),
      number: '',
    },
    emailAddress: {
      // businessPartnerNumber: "",
      newEmailAddress: '',
    },
  },
  billingAddress: {
    adressPortal: {
      changedCreateE03: true,
      city: '',
      housenumber: '',
      // id: "",
      postalCode: '',
      street: '',
    },
  },
};

const prepareValues = (data: BusinessPartnerWithContracts): Partial<BusinessUpdateParams> => {
  const {
    businessPartnerNumber, businessPartnerId,
    firstName, name, dateOfBirth, email, phoneNumber, phoneNumbers, legalForm, salutation, title, ...rest
  } = data;

  // const latestPhoneNumber = phoneNumbers?.length ? (
  //   phoneNumbers?.[phoneNumbers.length - 1].number
  // ) : phoneNumber;
  const latestPhoneNumber = getLatestBusinessPhoneNumber(phoneNumbers, phoneNumber);

  const salutationKey = Number(salutation);
  const titleKey = Number(title);

  return {
    personalData: {
      businessPartner: {
        businessPartnerNumber,
        newLegalForm: legalForm
          ? LEGAL_FORM_VALUES[Number(legalForm)]
          : 'None',
        newSalutation: !Number.isNaN(salutationKey) && salutationKey in SALUTATION_VALUES
          ? SALUTATION_VALUES[salutationKey]
          : 'None',
        newTitle: !Number.isNaN(titleKey) && titleKey in TITLE_VALUES
          ? TITLE_VALUES[titleKey]
          : 'None',
        newDateOfBirth: dayjs(dateOfBirth),
        newFirstname: firstName,
        newLastname: name,
      },
      emailAddress: {
        businessPartnerNumber,
        newEmailAddress: email,
      },
      phoneNumber: {
        businessPartner: businessPartnerId,
        number: latestPhoneNumber,
        creationDate: dateToSave(),
        advertisementConsentROList: [],
      },
    },
    billingAddress: {
      businessPartnerNumber,
      adressPortal: {
        changedCreateE03: true,
        city: rest.city,
        housenumber: rest.housenumber,
        housenumberAddition: rest.housenumberAddition || undefined,
        id: rest.address?.id,
        postalCode: rest.postalCode,
        street: rest.street,
        // const defaultValues: BillingAddressFormValues = {
        //   street: businessPartner.address?.street || "",
        //   houseNumber: businessPartner.address?.housenumber || "",
        //   houseNumberAddition: businessPartner.address?.housenumberAddition || "",
        //   postalCode: businessPartner.address?.postalCode || "",
        //   city: businessPartner.address?.city || "",
        // };
      },
    },
  };
};

export default function BusinessPartnerView(): React.ReactNode {
  const { id = '' } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { open, contextHolder } = useSimpleModal();
  const [form] = Form.useForm<BusinessUpdateParams>();
  const { user } = useAuth();
  const isReadOnly = user?.role === UserRole.MITARBEITER;

  const businessPartnerById = useBusinessPartnerGetById(id);
  // const loadBusinessPartner = useLoadBusinessPartner();

  const businessLastSyncAt = businessPartnerById.data?.synchronizedAt;

  const isSyncRequired = businessLastSyncAt
    ? dayjs().diff(dayjs(businessLastSyncAt), 'hour', true) > 1
    : true; // disable if no sync date

  const isFormDisabled = isReadOnly || isSyncRequired;

  const updateBusinessPartner = useUpdateBusinessPartner();
  const updateEmailAddress = useUpdateEmailAddress();
  const updatePhoneNumber = useUpdatePhoneNumber();
  const updateBillingAddress = useUpdateBillingAddress();

  const extendedBusinessRes: BusinessPartnerTableRow | null = businessPartnerById.data ? {
    key: businessPartnerById.data.id,
    fullName: `${businessPartnerById.data.firstName || '-'} ${businessPartnerById.data.name || '-'}`,
    ...businessPartnerById.data,
  } : null;

  /** Initial Values >>> */
  const { unsavedChanges, handleUnsavedChanges } = useUnsavedChanges();
  const [initialValues, setInitialValues] = useState(initialState); // <User>

  useEffect(() => {
    if (businessPartnerById.data && !businessPartnerById.loading && !businessPartnerById.error) {
      setInitialValues(prepareValues(businessPartnerById.data));
      // loadBusinessPartner.fetch({ businessPartnerNumber: businessPartnerById.data.businessPartnerNumber });
    }
  }, [businessPartnerById.data]);

  const fetchBusiness = () => businessPartnerById.fetch(undefined, id);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    handleUnsavedChanges(false);
  }, [initialValues]);
  /** <<< Initial Values */

  const handleSubmit = async (values: BusinessUpdateParams) => {
    handleUnsavedChanges(false);

    const requestsList: Promise<any>[] = [];

    /** If object changed - send request to save section */
    if (values.personalData?.businessPartner
      && !isObjEqual(values.personalData?.businessPartner || {}, initialValues.personalData?.businessPartner || {})) {
      const newDate = values.personalData.businessPartner?.newDateOfBirth;
      const queryParams = createSearchParamsString({
        ...values.personalData.businessPartner,
        newDateOfBirth: newDate ? newDate.utc(true).set('hours', 6).toISOString() : undefined,
      });

      requestsList.push(updateBusinessPartner.fetch(undefined, queryParams));
    }
    // NOTE: If phone is empty its not gonna send it
    if (values.personalData?.phoneNumber && values.personalData?.phoneNumber?.number
      && (values.personalData?.phoneNumber?.number !== '/')
      && !isObjEqual(values.personalData?.phoneNumber || {}, initialValues.personalData?.phoneNumber || {})) {
      requestsList.push(updatePhoneNumber.fetch(values.personalData.phoneNumber as IUpdateOrCreatePhoneNumberParams));
    }
    if (values.personalData?.emailAddress
      && !isObjEqual(values.personalData?.emailAddress || {}, initialValues.personalData?.emailAddress || {})) {
      const queryParams = createSearchParamsString(values.personalData.emailAddress);

      requestsList.push(updateEmailAddress.fetch(undefined, queryParams)
        .then((res) => {
          if (res) {
            message.success('Email change confirmation was sent to the user.', 10);
          }
        }));
    }

    if (values.billingAddress?.adressPortal?.city
      && !isObjEqual(values.billingAddress || {}, initialValues.billingAddress || {})) {
      const queryParams = createSearchParamsString({
        businessPartnerNumber: values.billingAddress?.businessPartnerNumber,
      });

      requestsList.push(updateBillingAddress.fetch(values.billingAddress.adressPortal, queryParams));
    }

    try {
      await Promise.all(requestsList);
      message.success('Changes saved successfully');
      fetchBusiness();
    } catch (error) {
      message.error('Failed to save changes. Please try again.');
      console.error('Error saving changes:', error);
    }
  };

  useMessageError(
    [businessPartnerById, updateBusinessPartner, updateEmailAddress, updatePhoneNumber, updateBillingAddress],
  );

  const handleFieldsChange = () => {
    if (!initialValues) return;
    handleUnsavedChanges(!isObjEqual(
      initialValues,
      form.getFieldsValue(),
    ));
  };

  return (
    <SinglePageContainer
      title={isReadOnly ? 'View Business partner' : 'Edit Business partner'}
      breadcrumbItems={[]}
      stickyHeader
      cardLoading={businessPartnerById.loading}
      extra={(
        <>
          {businessPartnerById.data?.status ? (
            <Tag color={getBusinessStatusColor(businessPartnerById.data?.status)} className="capitalize-first-letter">
              {businessPartnerById.data?.status}
            </Tag>
          ) : null}
          {extendedBusinessRes && !isReadOnly ? (
            <>
              <SyncBusinessButton
                record={extendedBusinessRes}
                buttonProps={{
                  type: 'default',
                  className: isSyncRequired ? 'animatedPulseButton' : undefined,
                }}
                afterSuccess={fetchBusiness}
              />
              <DeleteButton
                record={extendedBusinessRes}
                buttonProps={{ type: 'default' }}
                afterSuccess={() => navigate('/business-partners')}
              />
            </>
          ) : null}
          {!isReadOnly && (
            <Button
              type="primary"
              onClick={form.submit}
              icon={<SaveOutlined />}
              loading={businessPartnerById.loading
                || updateBusinessPartner.loading
                || updateEmailAddress.loading
                || updatePhoneNumber.loading
                || updateBillingAddress.loading}
              disabled={!unsavedChanges || isSyncRequired}
            >
              Save
            </Button>
          )}
        </>
      )}
    >
      <div style={{ maxWidth: '840px' }}>
        {contextHolder}
        <Form
          form={form}
          name="business-form"
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={initialValues}
          onFieldsChange={handleFieldsChange}
          disabled={isFormDisabled}
        >
          <SyncNotification isSyncRequired={!isReadOnly && isSyncRequired} />
          <BusinessFormItems data={initialValues} businessPartnerData={businessPartnerById.data} />
        </Form>

        <ContractsList businessPartnerData={businessPartnerById.data} isSyncRequired={isFormDisabled} />
      </div>
    </SinglePageContainer>
  );
}
